import React, { useEffect } from 'react';

/**
 * Responsive Performance Optimizer
 * Optimizes performance for all screen sizes: mobile, tablet, and desktop
 */
const MobileOptimizer: React.FC = () => {
  useEffect(() => {
    // IMMEDIATELY remove any padding on page load to prevent white space
    const removeInitialPadding = () => {
      const body = document.body;
      if (body.style.paddingBottom) {
        body.style.paddingBottom = '0px';
      }
      // Also remove any spacer divs
      const spacer = document.getElementById('consent-modal-spacer');
      if (spacer) {
        spacer.remove();
      }
    };
    
    // Run immediately
    removeInitialPadding();
    
    // Detect device type for responsive optimizations
    const getDeviceType = () => {
      const width = window.innerWidth;
      if (width < 640) return 'mobile'; // sm breakpoint
      if (width < 1024) return 'tablet'; // md-lg breakpoint
      return 'desktop'; // lg+ breakpoint
    };
    
    const deviceType = getDeviceType();
    const isMobile = deviceType === 'mobile';
    const isTablet = deviceType === 'tablet';
    const isDesktop = deviceType === 'desktop';

    // Responsive optimizations for all screen sizes
    const optimizeForDevice = () => {
      // 1. Reduce JavaScript bundle size by deferring non-critical scripts
      const deferNonCriticalScripts = () => {
        const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]');
        scripts.forEach(script => {
          // Defer all non-critical scripts on mobile
          if (script && !script.async && !script.defer && script.src && !script.src.includes('main') && !script.src.includes('index')) {
            script.defer = true;
          }
        });
      };
      
      // Defer i18n loading on mobile (not critical for initial render)
      const deferI18n = () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            // i18n is already lazy loaded, but ensure it's deferred
            const i18nChunks = document.querySelectorAll<HTMLScriptElement>('script[src*="vendor-i18n"], script[src*="i18n"]');
            i18nChunks.forEach(script => {
              if (script && !script.defer) script.defer = true;
            });
          }, { timeout: 1000 });
        }
      };

      // 2. Optimize images for all screen sizes
      const optimizeImages = () => {
        const images = document.querySelectorAll<HTMLImageElement>('img');
        images.forEach(img => {
          // Set loading priority - batch layout reads to prevent forced reflows
          requestAnimationFrame(() => {
            const rect = img.getBoundingClientRect();
            const isAboveFold = rect.top < window.innerHeight;
            
            // High priority for above-fold images on all devices
            if (isAboveFold) {
              img.setAttribute('fetchpriority', 'high');
            } else {
              // Low priority for below-fold images
              img.setAttribute('fetchpriority', 'low');
            }
            
            // Responsive image optimization
            if (isMobile) {
              // Mobile: aggressive lazy loading
              img.setAttribute('loading', 'lazy');
            } else if (isTablet) {
              // Tablet: moderate lazy loading
              if (!isAboveFold) {
                img.setAttribute('loading', 'lazy');
              }
            } else {
              // Desktop: lazy load only below-fold
              if (!isAboveFold) {
                img.setAttribute('loading', 'lazy');
              }
            }
            
            // Async decoding for all devices
            img.setAttribute('decoding', 'async');
          });
        });
      };

      // 3. Reduce layout shifts - CRITICAL for all screen sizes
      const preventLayoutShift = () => {
        // CRITICAL: Reserve space for consent modals ONLY when they appear to prevent CLS
        const reserveConsentSpace = () => {
          const body = document.body;
          const fcRoot = document.querySelector('.fc-consent-root, .fc-dialog-container, [class*="fc-"]');
          const cookieBanner = document.querySelector('.cookie-banner-visible');
          
          // Only add padding when consent modals are actually present
          // Responsive padding based on screen size
          if (fcRoot || cookieBanner) {
            let padding = '200px'; // Desktop default
            if (isMobile) {
              padding = '150px';
            } else if (isTablet) {
              padding = '180px';
            }
            if (body.style.paddingBottom !== padding) {
              body.style.paddingBottom = padding;
              body.style.transition = 'padding-bottom 0.3s ease';
            }
          } else {
            // Remove padding when no modals are present
            if (body.style.paddingBottom && body.style.paddingBottom !== '0px') {
              body.style.paddingBottom = '0px';
            }
          }
        };
        
        // Monitor for consent modals appearing/disappearing and adjust padding dynamically
        const setupObserver = () => {
          try {
            if (!document.body || !(document.body instanceof Node)) {
              setTimeout(setupObserver, 100);
              return;
            }
            
            const observer = new MutationObserver(() => {
              reserveConsentSpace();
            });
            
            // Run immediately to check for existing modals
            reserveConsentSpace();
            
            // Monitor for consent modals appearing/disappearing
            observer.observe(document.body, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ['class', 'style']
            });
          } catch (error) {
            // Fallback: just use the interval
            console.debug('MutationObserver setup failed, using interval fallback:', error);
          }
        };
        
        // Wait for body to be ready
        if (document.body) {
          setupObserver();
        } else {
          document.addEventListener('DOMContentLoaded', setupObserver);
        }
        
        // Also check periodically (fallback for modals that load late)
        const checkInterval = setInterval(() => {
          reserveConsentSpace();
        }, 500);
        
        // Cleanup interval after 10 seconds (modals should have appeared by then)
        setTimeout(() => {
          clearInterval(checkInterval);
        }, 10000);
        
        // Add aspect ratio to images
        const images = document.querySelectorAll<HTMLImageElement>('img:not([style*="aspect-ratio"])');
        images.forEach(img => {
          if (img && img.naturalWidth && img.naturalHeight) {
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            img.style.aspectRatio = `${aspectRatio}`;
          }
        });
      };

      // 4. Optimize fonts for mobile
      const optimizeFonts = () => {
        // Preload critical fonts
        const fontLink = document.createElement('link');
        fontLink.rel = 'preload';
        fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
        fontLink.as = 'style';
        fontLink.onload = () => {
          fontLink.rel = 'stylesheet';
        };
        document.head.appendChild(fontLink);
      };

      // 5. Reduce main thread blocking
      const optimizeMainThread = () => {
        // Defer Google FundingChoices consent modal to prevent CLS
        const deferConsentModal = () => {
          // Delay FundingChoices script loading until after initial render
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              // FundingChoices is loaded by AdSense, defer it
              const fcScripts = document.querySelectorAll<HTMLScriptElement>('script[src*="fundingchoices"]');
              fcScripts.forEach(script => {
                if (script && !script.defer && !script.async) {
                  script.defer = true;
                }
              });
            }, { timeout: 3000 });
          }
        };
        
        deferConsentModal();
        
        // Use requestIdleCallback for non-critical tasks
        if ('requestIdleCallback' in window) {
          const runWhenIdle = (callback: () => void) => {
            requestIdleCallback(callback, { timeout: 2000 });
          };
          
          // Defer non-critical operations
          runWhenIdle(() => {
            // Analytics, tracking, etc.
            console.log('Running non-critical mobile optimizations');
          });
        }
      };

      // 6. Optimize touch interactions (mobile and tablet)
      const optimizeTouch = () => {
        if (isMobile || isTablet) {
          // Add touch-action CSS for better scrolling on touch devices
          const style = document.createElement('style');
          style.textContent = `
            * {
              touch-action: manipulation;
            }
            button, a {
              -webkit-tap-highlight-color: transparent;
            }
          `;
          document.head.appendChild(style);
        }
      };

      // 7. Reduce memory usage
      const optimizeMemory = () => {
        // Clean up unused event listeners
        const cleanup = () => {
          // Remove old event listeners
          window.removeEventListener('resize', () => {});
          window.removeEventListener('scroll', () => {});
        };
        
        // Run cleanup periodically
        setInterval(cleanup, 30000); // Every 30 seconds
      };

      // Initialize all optimizations
      deferNonCriticalScripts();
      deferI18n(); // Call deferI18n to fix unused warning
      optimizeImages();
      preventLayoutShift();
      optimizeFonts();
      optimizeMainThread();
      optimizeTouch();
      optimizeMemory();
    };

    // Run optimizations for all device types
    optimizeForDevice();

    // Re-optimize on route changes
    const setupImageObserver = () => {
      try {
        if (!document.body || !(document.body instanceof Node)) {
          setTimeout(setupImageObserver, 100);
          return;
        }
        
        const observer = new MutationObserver(() => {
          const images = document.querySelectorAll<HTMLImageElement>('img:not([loading])');
          images.forEach(img => {
            img.setAttribute('loading', 'lazy');
            img.setAttribute('decoding', 'async');
          });
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        return () => observer.disconnect();
      } catch (error) {
        console.debug('Image observer setup failed:', error);
        return () => {}; // Return empty cleanup function
      }
    };
    
    let cleanup: (() => void) | undefined;
    
    // Wait for body to be ready
    if (document.body) {
      cleanup = setupImageObserver();
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        cleanup = setupImageObserver();
      });
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  return null;
};

export default MobileOptimizer;
